import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

// Tipos de usuário do sistema
export type UserRole = 'admin' | 'photographer' | 'user';

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  avatar?: string;
  tenantId?: string;
}

interface LoginCredentials {
  email: string;
  password: string;
}

interface RegisterData {
  name: string;
  email: string;
  password: string;
}

export const useAuthStore = defineStore('auth', () => {
  // Estado
  const user = ref<User | null>(null);
  const token = ref<string | null>(localStorage.getItem('accessToken'));
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const redirectAfterLogin = ref<string | null>(localStorage.getItem('redirectAfterLogin'));

  // Computed
  const isAuthenticated = computed(() => !!user.value && !!token.value);
  const isAdmin = computed(() => user.value?.role === 'admin');
  const isPhotographer = computed(() => user.value?.role === 'photographer');
  const userRole = computed(() => user.value?.role || null);
  const userName = computed(() => user.value?.name || '');
  const userEmail = computed(() => user.value?.email || '');
  
  // Rota padrão baseada no role
  const defaultRoute = computed(() => {
    // Se há uma rota salva para redirect, usa ela
    if (redirectAfterLogin.value) {
      const route = redirectAfterLogin.value;
      // Limpa o redirect após usar
      redirectAfterLogin.value = null;
      localStorage.removeItem('redirectAfterLogin');
      return route;
    }
    
    if (!user.value) return '/login';
    switch (user.value.role) {
      case 'admin': return '/admin';
      case 'photographer': return '/studio';
      default: return '/';
    }
  });
  
  // Salvar rota para redirect após login
  const setRedirectAfterLogin = (route: string) => {
    redirectAfterLogin.value = route;
    localStorage.setItem('redirectAfterLogin', route);
  };
  
  const clearRedirectAfterLogin = () => {
    redirectAfterLogin.value = null;
    localStorage.removeItem('redirectAfterLogin');
  };

  // Actions
  const login = async (credentials: LoginCredentials): Promise<boolean> => {
    isLoading.value = true;
    error.value = null;

    try {
      console.log('🔐 [AUTH] Iniciando login...');
      console.log('📧 Email:', credentials.email);
      console.log('🌐 URL:', '/api/v1/auth/login');
      
      const response = await fetch('/api/v1/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      console.log('📡 [AUTH] Status da resposta:', response.status, response.statusText);
      console.log('📋 [AUTH] Headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ [AUTH] Erro na resposta:', errorData);
        throw new Error(errorData.message || errorData.error || 'Credenciais inválidas');
      }

      const data = await response.json();
      console.log('✅ [AUTH] Login bem-sucedido!');
      console.log('👤 [AUTH] Dados recebidos:', { hasToken: !!data.accessToken, hasUser: !!data.user });
      
      // Salvar token
      token.value = data.accessToken;
      localStorage.setItem('accessToken', data.accessToken);
      console.log('💾 [AUTH] Token salvo no localStorage');
      
      // Salvar dados do usuário (extrair do token se não vier no response)
      if (data.user) {
        user.value = {
          id: data.user.id,
          email: data.user.email,
          role: data.user.role,
          name: data.user.name || data.user.email.split('@')[0],
        };
        localStorage.setItem('user', JSON.stringify(user.value));
        console.log('💾 [AUTH] Usuário salvo:', user.value.email, '- Role:', user.value.role);
      } else {
        // Decodificar token JWT para obter dados do usuário
        const payload = JSON.parse(atob(data.accessToken.split('.')[1]));
        user.value = {
          id: payload.userId,
          email: payload.email,
          role: payload.role,
          name: payload.email.split('@')[0],
        };
        localStorage.setItem('user', JSON.stringify(user.value));
        console.log('💾 [AUTH] Usuário extraído do token:', user.value.email, '- Role:', user.value.role);
      }

      console.log('🎉 [AUTH] Login completo! Redirecionando...');
      return true;
    } catch (err) {
      console.error('❌ [AUTH] Erro no login:', err);
      error.value = err instanceof Error ? err.message : 'Erro no login';
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const register = async (userData: RegisterData): Promise<boolean> => {
    isLoading.value = true;
    error.value = null;

    try {
      // O backend espera firstName e lastName separados
      const fullName = (userData.name || '').trim().replace(/\s+/g, ' ');
      const parts = fullName.split(' ');
      const firstName = parts.shift() || '';
      const lastName = parts.join(' ') || firstName;

      const response = await fetch('/api/v1/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: userData.email,
          password: userData.password,
          firstName,
          lastName,
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        const msg = errData.message || errData.error || 'Erro ao criar conta';
        throw new Error(Array.isArray(msg) ? msg.join('. ') : msg);
      }

      const data = await response.json();
      
      // Auto-login após registro
      token.value = data.accessToken;
      localStorage.setItem('accessToken', data.accessToken);
      
      user.value = {
        id: data.user.id,
        email: data.user.email,
        role: data.user.role,
        name: data.user.name || data.user.email.split('@')[0],
      };
      localStorage.setItem('user', JSON.stringify(user.value));

      return true;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Erro no registro';
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const logout = (): void => {
    user.value = null;
    token.value = null;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    error.value = null;
  };

  const loadUserFromStorage = (): void => {
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('accessToken');

    if (storedUser && storedToken) {
      try {
        user.value = JSON.parse(storedUser);
        token.value = storedToken;
      } catch (err) {
        console.error('Erro ao carregar usuário do localStorage:', err);
        logout();
      }
    }
  };

  const updateProfile = async (profileData: Partial<User>): Promise<boolean> => {
    if (!user.value) return false;

    isLoading.value = true;
    error.value = null;

    try {
      // O backend expõe o perfil do usuário logado em /users/me.
      // /auth/profile não existe — a atualização de perfil devolvia 404.
      const response = await fetch('/api/v1/users/me', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token.value}`,
        },
        body: JSON.stringify(profileData),
      });

      if (!response.ok) {
        throw new Error('Erro ao atualizar perfil');
      }

      const updatedUser = await response.json();
      user.value = { ...user.value, ...updatedUser };
      localStorage.setItem('user', JSON.stringify(user.value));

      return true;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Erro ao atualizar perfil';
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string): Promise<boolean> => {
    isLoading.value = true;
    error.value = null;

    try {
      // O backend expõe PUT em /auth/change-password. Com POST vinha 404 e a
      // troca de senha na área do fotógrafo nunca funcionava.
      const response = await fetch('/api/v1/auth/change-password', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token.value}`,
        },
        body: JSON.stringify({
          currentPassword,
          newPassword,
        }),
      });

      if (!response.ok) {
        throw new Error('Erro ao alterar senha');
      }

      return true;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Erro ao alterar senha';
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const refreshToken = async (): Promise<boolean> => {
    if (!token.value) return false;

    try {
      const response = await fetch('/api/v1/auth/refresh', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token.value}`,
        },
      });

      if (!response.ok) {
        logout();
        return false;
      }

      const data = await response.json();
      // O backend devolve `accessToken` (mesmo formato do login). Antes lia
      // `data.token`, que vinha undefined e apagava a sessão a cada renovação.
      const newToken = data.accessToken || data.token;
      if (!newToken) {
        logout();
        return false;
      }
      token.value = newToken;
      localStorage.setItem('accessToken', newToken);

      return true;
    } catch (err) {
      console.error('Erro ao renovar token:', err);
      logout();
      return false;
    }
  };

  const clearError = (): void => {
    error.value = null;
  };

  /**
   * Lê o `exp` do JWT sem depender de biblioteca.
   * Retorna null quando o token não tem formato válido.
   */
  const getTokenExpiry = (jwt: string | null): number | null => {
    if (!jwt) return null;
    try {
      const payload = JSON.parse(atob(jwt.split('.')[1]));
      return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
    } catch {
      return null;
    }
  };

  const isSessionExpired = computed(() => {
    const exp = getTokenExpiry(token.value);
    return exp !== null && exp <= Date.now();
  });

  /**
   * Renova o token quando falta menos de 1 dia para expirar.
   * Evita o fotógrafo ser jogado para o login no meio da diagramação.
   */
  const ensureFreshSession = async (): Promise<void> => {
    const exp = getTokenExpiry(token.value);
    if (exp === null) return;
    const umDia = 24 * 60 * 60 * 1000;
    if (exp - Date.now() < umDia) {
      await refreshToken();
    }
  };

  // Inicializar store
  const initialize = (): void => {
    loadUserFromStorage();

    // Sessão vencida guardada no navegador fazia o app parecer logado enquanto
    // toda chamada à API voltava 401 — inclusive os salvamentos do editor.
    if (token.value && isSessionExpired.value) {
      logout();
      return;
    }

    if (token.value) {
      void ensureFreshSession();
    }
  };

  return {
    // Estado
    user,
    token,
    isLoading,
    error,

    // Computed
    isAuthenticated,
    isAdmin,
    isPhotographer,
    userRole,
    defaultRoute,
    userName,
    userEmail,

    // Actions
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    refreshToken,
    ensureFreshSession,
    isSessionExpired,
    clearError,
    initialize,
    setRedirectAfterLogin,
    clearRedirectAfterLogin,
  };
});